import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Form, Alert, Spinner, Pagination, Modal, InputGroup } from 'react-bootstrap';
import { getInvoices, updatePaymentStatus, updatePaymentDate, sendInvoice, getInvoiceHistorique } from './DataService';
import { format } from 'date-fns';
import './InvoicesList.css';

const InvoicesList = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // KPIs pour factures impayées
  const [unpaidKpis, setUnpaidKpis] = useState({
    count: 0,
    totalAmount: 0,
    oldestDays: 0
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalInvoices, setTotalInvoices] = useState(0);

  // Filtres
  const currentYear = new Date().getFullYear();
  const [filters, setFilters] = useState({
    statuFacture: null,
    year: null,
    month: null,
    search: ''
  });

  // Modal pour envoyer une facture
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Modal d'historique : qui a fait quoi, et quand. Le journal existait depuis
  // l'incident des six factures marquees payees a une ligne pres ; rien ne le
  // lisait.
  const [showHistoriqueModal, setShowHistoriqueModal] = useState(false);
  const [historique, setHistorique] = useState(null);
  const [historiqueFacture, setHistoriqueFacture] = useState(null);
  const [historiqueLoading, setHistoriqueLoading] = useState(false);
  const [historiqueError, setHistoriqueError] = useState(null);
  // Pourquoi aucune relance ne partira (client exclu), redige par le serveur.
  const [historiqueAvertissement, setHistoriqueAvertissement] = useState(null);

  // Modal pour corriger la date de paiement
  const [showPaymentDateModal, setShowPaymentDateModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [paymentDateInput, setPaymentDateInput] = useState('');
  const [savingPaymentDate, setSavingPaymentDate] = useState(false);

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  useEffect(() => {
    fetchInvoices();
  }, [currentPage, perPage, filters]);

  const calculateDaysOverdue = (dateCreation) => {
    const createdDate = new Date(dateCreation);
    const today = new Date();
    const diffTime = today - createdDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const fetchInvoices = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getInvoices({
        page: currentPage,
        nbpage: perPage,
        ...filters
      });

      const invoicesData = data.data || [];

      // Calculer le retard pour chaque facture impayée
      const invoicesWithOverdue = invoicesData.map(invoice => ({
        ...invoice,
        days_overdue: invoice.statu_facture === 1 ? calculateDaysOverdue(invoice.date_creation_iso || invoice.date_creation) : null
      }));

      setInvoices(invoicesWithOverdue);
      setTotalPages(data.last_page || 1);
      setTotalInvoices(data.total || 0);

      // Calculer les KPIs pour les factures impayées
      const unpaidInvoices = invoicesWithOverdue.filter(inv => inv.statu_facture === 1);
      const totalUnpaidAmount = unpaidInvoices.reduce((sum, inv) => sum + (inv.total_ttc || 0), 0);
      const oldestDays = unpaidInvoices.length > 0
        ? Math.max(...unpaidInvoices.map(inv => inv.days_overdue || 0))
        : 0;

      setUnpaidKpis({
        count: unpaidInvoices.length,
        totalAmount: totalUnpaidAmount,
        oldestDays: oldestDays
      });

    } catch (err) {
      setError('Erreur lors de la récupération des factures');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePaymentStatus = async (numFacture, currentStatus) => {
    const newStatus = currentStatus === 1 ? 2 : 1;

    try {
      const result = await updatePaymentStatus(numFacture, newStatus);
      if (result.success) {
        setSuccess(`Statut de paiement mis à jour pour la facture ${numFacture}`);
        fetchInvoices(); // Rafraîchir la liste
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la mise à jour du statut');
      console.error(err);
    }
  };

  const handleSendInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowSendModal(true);
  };

  const handleShowHistorique = async (invoice) => {
    setHistoriqueFacture(invoice.num_facture);
    setHistorique(null);
    setHistoriqueError(null);
    setHistoriqueAvertissement(null);
    setHistoriqueLoading(true);
    setShowHistoriqueModal(true);

    try {
      const result = await getInvoiceHistorique(invoice.num_facture);
      setHistorique(result.evenements || []);
      setHistoriqueAvertissement(result.avertissement || null);
    } catch (e) {
      console.error('Erreur chargement historique:', e);
      setHistoriqueError("Impossible de charger l'historique de cette facture");
    } finally {
      setHistoriqueLoading(false);
    }
  };

  const handleEditPaymentDate = (invoice) => {
    setEditingInvoice(invoice);
    setPaymentDateInput(invoice.paid_at || '');
    setShowPaymentDateModal(true);
  };

  const confirmUpdatePaymentDate = async () => {
    if (!editingInvoice || !paymentDateInput) return;

    setSavingPaymentDate(true);
    setError(null);
    try {
      const result = await updatePaymentDate(editingInvoice.num_facture, paymentDateInput);
      if (result.success) {
        setSuccess(`Date de paiement mise à jour pour la facture ${editingInvoice.num_facture}`);
        setShowPaymentDateModal(false);
        setEditingInvoice(null);
        fetchInvoices();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la mise à jour de la date de paiement');
      console.error(err);
    } finally {
      setSavingPaymentDate(false);
    }
  };

  const confirmSendInvoice = async () => {
    if (!selectedInvoice) return;

    setSending(true);
    setError(null);
    try {
      const result = await sendInvoice(
        selectedInvoice.num_facture,
        selectedInvoice.client_id,
        customMessage || null
      );

      if (result.success) {
        setSuccess(`Facture envoyée avec succès à ${result.email}`);
        setShowSendModal(false);
        setCustomMessage('');
        setSelectedInvoice(null);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'envoi de la facture');
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset à la première page
  };

  const getPaymentStatusBadge = (status) => {
    return status === 2 ? (
      <Badge bg="success">Payé</Badge>
    ) : (
      <Badge bg="danger">Non payé</Badge>
    );
  };

  const getOverdueBadge = (daysOverdue) => {
    if (daysOverdue === null || daysOverdue === undefined) return null;

    let variant = 'success';
    if (daysOverdue > 14) {
      variant = 'danger';
    } else if (daysOverdue >= 7) {
      variant = 'warning';
    }

    const weeks = Math.floor(daysOverdue / 7);

    return (
      <Badge bg={variant}>
        {daysOverdue} j{weeks > 0 ? ` (${weeks} sem.)` : ''}
      </Badge>
    );
  };

  const renderPagination = () => {
    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.First key="first" onClick={() => setCurrentPage(1)} />,
        <Pagination.Ellipsis key="ellipsis-start" disabled />
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      items.push(
        <Pagination.Ellipsis key="ellipsis-end" disabled />,
        <Pagination.Last key="last" onClick={() => setCurrentPage(totalPages)} />
      );
    }

    return items;
  };

  return (
    <Container fluid className="invoices-list-container py-4">
      <Row className="mb-4">
        <Col>
          <h2>Liste des factures</h2>
          <p className="text-muted">Consultez et gérez toutes les factures mensuelles générées</p>
        </Col>
      </Row>

      {/* KPI Cards - Uniquement si des factures impayées */}
      {unpaidKpis.count > 0 && (
        <Row className="mb-4">
          <Col md={4}>
            <Card className="text-center border-warning">
              <Card.Body>
                <h6 className="text-muted text-uppercase mb-2">Factures impayées</h6>
                <h2 className="mb-0 text-warning">{unpaidKpis.count}</h2>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="text-center border-warning">
              <Card.Body>
                <h6 className="text-muted text-uppercase mb-2">Montant total impayé</h6>
                <h2 className="mb-0 text-warning">
                  {unpaidKpis.totalAmount.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
                </h2>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="text-center border-warning">
              <Card.Body>
                <h6 className="text-muted text-uppercase mb-2">Retard le plus ancien</h6>
                <h2 className="mb-0 text-warning">{unpaidKpis.oldestDays} jours</h2>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Filtres */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-end">
            <Col md={2}>
              <Form.Group>
                <Form.Label>Statut</Form.Label>
                <Form.Select
                  value={filters.statuFacture || ''}
                  onChange={(e) => handleFilterChange('statuFacture', e.target.value ? parseInt(e.target.value) : null)}
                >
                  <option value="">Tous</option>
                  <option value="1">Non payé</option>
                  <option value="2">Payé</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Année</Form.Label>
                <Form.Select
                  value={filters.year || ''}
                  onChange={(e) => handleFilterChange('year', e.target.value ? parseInt(e.target.value) : null)}
                >
                  <option value="">Toutes</option>
                  {[currentYear, currentYear - 1, currentYear - 2].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Mois</Form.Label>
                <Form.Select
                  value={filters.month || ''}
                  onChange={(e) => handleFilterChange('month', e.target.value ? parseInt(e.target.value) : null)}
                >
                  <option value="">Tous</option>
                  {monthNames.map((month, index) => (
                    <option key={index + 1} value={index + 1}>{month}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Recherche</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="N° facture, client, email..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                  />
                  {filters.search && (
                    <Button
                      variant="outline-secondary"
                      onClick={() => handleFilterChange('search', '')}
                    >
                      ✕
                    </Button>
                  )}
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Résultats par page</Form.Label>
                <Form.Select
                  value={perPage}
                  onChange={(e) => {
                    setPerPage(parseInt(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={1}>
              <Button variant="primary" onClick={fetchInvoices} disabled={loading}>
                {loading ? <Spinner animation="border" size="sm" /> : '↻'}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Messages */}
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess(null)} dismissible>{success}</Alert>}

      {/* Compteur */}
      <div className="mb-3">
        <small className="text-muted">
          {totalInvoices} facture{totalInvoices > 1 ? 's' : ''} trouvée{totalInvoices > 1 ? 's' : ''}
        </small>
      </div>

      {/* Table des factures */}
      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Chargement...</span>
              </Spinner>
            </div>
          ) : invoices.length === 0 ? (
            <Alert variant="info" className="m-4">
              Aucune facture trouvée
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table striped hover className="mb-0">
                <thead className="table-dark">
                  <tr>
                    <th>N° Facture</th>
                    <th>Date</th>
                    <th>Client</th>
                    <th>Email</th>
                    <th>Commandes</th>
                    <th>Montant TTC</th>
                    <th>Statut</th>
                    <th>Date de paiement</th>
                    <th>Retard</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice, index) => (
                    <tr key={index}>
                      <td>
                        <strong>{invoice.num_facture}</strong>
                      </td>
                      <td>{invoice.date_creation}</td>
                      <td>{invoice.client_name}</td>
                      <td>
                        <small>{invoice.client_email}</small>
                      </td>
                      <td>
                        <Badge bg="secondary">{invoice.orders_count}</Badge>
                      </td>
                      <td>
                        <strong>{invoice.total_ttc.toFixed(2)} €</strong>
                      </td>
                      <td>{getPaymentStatusBadge(invoice.statu_facture)}</td>
                      <td>
                        {invoice.statu_facture === 2 ? (
                          <div className="d-flex align-items-center gap-2">
                            <span>{invoice.paid_at || '—'}</span>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0"
                              title="Corriger la date de paiement"
                              onClick={() => handleEditPaymentDate(invoice)}
                            >
                              ✏️
                            </Button>
                          </div>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        {invoice.statu_facture === 1 ? getOverdueBadge(invoice.days_overdue) : <span className="text-muted">-</span>}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          {invoice.pdf_url && invoice.pdf_url.trim() !== '' && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              href={invoice.pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              PDF
                            </Button>
                          )}
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => handleSendInvoice(invoice)}
                          >
                            Envoyer
                          </Button>
                          <Button
                            variant={invoice.statu_facture === 2 ? 'outline-warning' : 'outline-success'}
                            size="sm"
                            onClick={() => handleUpdatePaymentStatus(invoice.num_facture, invoice.statu_facture)}
                          >
                            {invoice.statu_facture === 2 ? 'Non payé' : 'Payé'}
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            title="Qui a fait quoi, et quand"
                            onClick={() => handleShowHistorique(invoice)}
                          >
                            Historique
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>{renderPagination()}</Pagination>
        </div>
      )}

      {/* Modal d'envoi de facture */}
      <Modal show={showSendModal} onHide={() => setShowSendModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Envoyer la facture par email</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedInvoice && (
            <>
              <p>
                Envoyer la facture <strong>{selectedInvoice.num_facture}</strong> par email à :
              </p>
              <ul>
                <li><strong>Client :</strong> {selectedInvoice.client_name}</li>
                <li><strong>Email :</strong> {selectedInvoice.client_email}</li>
                <li><strong>Montant :</strong> {selectedInvoice.total_ttc.toFixed(2)} €</li>
              </ul>
              <Form.Group className="mb-3">
                <Form.Label>Message personnalisé (optionnel)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Ajoutez un message personnalisé à l'email..."
                  maxLength={1000}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSendModal(false)} disabled={sending}>
            Annuler
          </Button>
          <Button variant="primary" onClick={confirmSendInvoice} disabled={sending}>
            {sending ? <Spinner animation="border" size="sm" /> : 'Envoyer'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de correction de la date de paiement */}
      <Modal show={showPaymentDateModal} onHide={() => setShowPaymentDateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Corriger la date de paiement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingInvoice && (
            <>
              <p>
                Facture <strong>{editingInvoice.num_facture}</strong> — {editingInvoice.client_name}
              </p>
              <Form.Group className="mb-3">
                <Form.Label>Date de paiement</Form.Label>
                <Form.Control
                  type="date"
                  value={paymentDateInput}
                  onChange={(e) => setPaymentDateInput(e.target.value)}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPaymentDateModal(false)} disabled={savingPaymentDate}>
            Annuler
          </Button>
          <Button variant="primary" onClick={confirmUpdatePaymentDate} disabled={savingPaymentDate || !paymentDateInput}>
            {savingPaymentDate ? <Spinner animation="border" size="sm" /> : 'Enregistrer'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Historique d'une facture.

          Les libelles viennent du serveur : `action` et `origine` sont des codes
          internes, et les traduire ici aussi les ferait diverger au premier acte
          ajoute — c'est l'acte nouveau qui s'afficherait alors sous son code brut. */}
      <Modal show={showHistoriqueModal} onHide={() => setShowHistoriqueModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Historique · facture {historiqueFacture}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {historiqueLoading && (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          )}

          {historiqueError && <Alert variant="warning" className="py-2">{historiqueError}</Alert>}

          {historiqueAvertissement && (
            <Alert variant="info" className="py-2">{historiqueAvertissement}</Alert>
          )}

          {!historiqueLoading && !historiqueError && historique && historique.length === 0 && (
            <p className="text-muted mb-0">Aucun evenement enregistre pour cette facture.</p>
          )}

          {!historiqueLoading && historique && historique.length > 0 && (
            <Table size="sm" className="align-middle mb-0">
              <tbody>
                {historique.map((e, i) => (
                  <tr key={`${e.horodatage}-${e.action}-${i}`}>
                    <td style={{ whiteSpace: 'nowrap', width: 135 }} className="text-muted">
                      {/* La chaine servie telle quelle : la reconvertir par
                          `new Date()` afficherait une autre heure depuis un
                          navigateur regle sur un autre fuseau. */}
                      {e.affichage || '—'}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', width: 130 }}>
                      {/* « systeme » quand l'acte ne vient de personne : un
                          encaissement carte, une relance planifiee. Une emission
                          ancienne, elle, le dit autrement dans ses details. */}
                      {e.auteur
                        ? e.auteur
                        : <span className="text-muted fst-italic">systeme</span>}
                    </td>
                    <td>
                      <div>{e.libelle}</div>
                      {e.details && (
                        <div className="text-muted" style={{ fontSize: 12 }}>{e.details}</div>
                      )}
                      {/* Le symptome meme qui a motive ce journal : six factures
                          marquees payees a une ligne pres. Il doit sauter aux yeux. */}
                      {e.alerte && (
                        <div className="text-danger" style={{ fontSize: 12 }}>⚠ {e.alerte}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHistoriqueModal(false)}>
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default InvoicesList;
